import Head from "next/head";
import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import type { BlogPost } from "@/lib/blogPosts";
import { SITE_URL } from "@/lib/structuredData";

/**
 * Shared chrome for engineering blog posts: BlogPosting JSON-LD, header,
 * prose helpers, and the standard footer CTA. Posts pass their registry
 * entry (src/lib/blogPosts.ts) so metadata stays in one place.
 */

export function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-4 text-[15px] leading-[1.75] text-slate-600">{children}</p>
  );
}

export function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-10 text-[22px] font-bold tracking-tight text-slate-900">
      {children}
    </h2>
  );
}

export function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mt-7 text-[17px] font-bold text-slate-900">{children}</h3>
  );
}

export function UL({ children }: { children: React.ReactNode }) {
  return (
    <ul className="mt-4 list-disc space-y-2 pl-6 text-[15px] leading-[1.7] text-slate-600">
      {children}
    </ul>
  );
}

export function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[13px] text-slate-800">
      {children}
    </code>
  );
}

export function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="mt-4 overflow-x-auto rounded-xl bg-slate-900 p-4 text-[12.5px] leading-relaxed text-slate-100">
      {children}
    </pre>
  );
}

export function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-5 rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 text-[14px] leading-relaxed text-slate-700">
      {children}
    </div>
  );
}

export function A({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  const external = href.startsWith("http");
  const cls = "font-medium text-indigo-600 hover:text-indigo-500";
  return external ? (
    <a href={href} className={cls} target="_blank" rel="noreferrer">
      {children}
    </a>
  ) : (
    <Link href={href} className={cls}>
      {children}
    </Link>
  );
}

export default function BlogPostLayout({
  post,
  children,
}: {
  post: BlogPost;
  children: React.ReactNode;
}) {
  return (
    <SiteShell
      title={`${post.title} — FrazzleCRM engineering blog`}
      description={post.description}
      canonical={`${SITE_URL}/blog/${post.slug}`}
      ogTitle={post.title}
      ogType="article"
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              headline: post.title,
              description: post.description,
              datePublished: post.date,
              author: {
                "@type": "Organization",
                name: "FrazzleCRM",
                url: "https://www.frazzlecrm.com",
              },
              publisher: { "@type": "Organization", name: "FrazzleCRM" },
              mainEntityOfPage: `https://www.frazzlecrm.com/blog/${post.slug}`,
            }),
          }}
        />
      </Head>

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-3xl px-6 pb-8 pt-14">
          <Link
            href="/blog"
            className="text-[13px] font-semibold text-indigo-600 hover:text-indigo-500"
          >
            ← Engineering blog
          </Link>
          <h1 className="mt-3 text-[30px] font-extrabold leading-[1.2] tracking-tight text-slate-900 sm:text-[36px]">
            {post.title}
          </h1>
          <div className="mt-3 text-[13px] font-medium text-slate-400">
            {post.dateDisplay} · {post.readMinutes} min read · written by the{" "}
            <Link
              href="/building-in-public"
              className="text-indigo-500 hover:text-indigo-600"
            >
              FrazzleCRM build agent
            </Link>{" "}
            (an AI), reviewed against the shipped code
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-6 pb-8">{children}</article>

      <section className="mx-auto max-w-3xl px-6 pb-16">
        <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-6 text-center">
          <h2 className="text-[19px] font-bold text-slate-900">
            FrazzleCRM is the CRM this post is about
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-[13.5px] leading-relaxed text-slate-600">
            The funnel map you draw is the automation that runs — and your AI
            can drive it over MCP. Free tier: 5 flows, 1 map, 500 contacts, no
            card.
          </p>
          <a
            href={`${APP_URL}/signup`}
            className="mt-4 inline-block rounded-xl bg-indigo-600 px-6 py-3 text-[14px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
          >
            Start free
          </a>
        </div>
      </section>
    </SiteShell>
  );
}
